import React, { useState, useEffect } from 'react';
import {
  Dialog, Box, Typography, IconButton, LinearProgress, Button,
  Slide, Chip,
} from '@mui/material';
import { Close, ArrowBack, ArrowForward, Check } from '@mui/icons-material';

export default function CookingModeDialog({ open, onClose, recipe }) {
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (open) setStep(0);
  }, [open]);

  // Wake Lock — keep screen on while cooking
  useEffect(() => {
    if (!open || !('wakeLock' in navigator)) return;
    let lock = null;
    navigator.wakeLock.request('screen').then((l) => { lock = l; }).catch(() => {});
    return () => { lock?.release(); };
  }, [open]);

  if (!recipe) return null;
  const instructions = recipe.instructions || [];
  const ingredients = recipe.ingredients || [];
  const total = instructions.length;
  const current = instructions[step];
  const isLast = step === total - 1;
  const isDone = step >= total;

  const progress = total > 0 ? ((step + (isDone ? 1 : 0)) / total) * 100 : 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      TransitionComponent={Slide}
      TransitionProps={{ direction: 'up' }}
      PaperProps={{ sx: { bgcolor: '#0c0b09' } }}
    >
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', px: 2, py: 1.5,
        borderBottom: '1px solid rgba(255,255,255,0.07)',
      }}>
        <IconButton onClick={onClose} sx={{ mr: 1.5 }}>
          <Close />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={700} noWrap>{recipe.title}</Typography>
          {!isDone && (
            <Typography variant="caption" color="text.secondary">
              Step {step + 1} of {total}
            </Typography>
          )}
        </Box>
        {recipe.servings > 0 && (
          <Chip
            label={`Serves ${recipe.servings}`}
            size="small"
            sx={{ bgcolor: 'rgba(255,255,255,0.07)', fontSize: '0.75rem' }}
          />
        )}
      </Box>

      {/* Progress bar */}
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 3,
          bgcolor: 'rgba(255,255,255,0.06)',
          '& .MuiLinearProgress-bar': { bgcolor: 'primary.main' },
        }}
      />

      {/* Content */}
      <Box sx={{
        flex: 1, display: 'flex', flexDirection: 'column',
        px: { xs: 3, md: 8 }, py: { xs: 4, md: 6 },
        maxWidth: 720, mx: 'auto', width: '100%',
        overflowY: 'auto',
      }}>
        {isDone ? (
          /* Done screen */
          <Box sx={{ textAlign: 'center', my: 'auto' }}>
            <Typography sx={{ fontSize: '5rem', mb: 2 }}>🎉</Typography>
            <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>All done!</Typography>
            <Typography color="text.secondary" sx={{ mb: 4 }}>
              {recipe.title} is ready. Enjoy!
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={onClose}
              sx={{ background: 'linear-gradient(135deg, #f0873c, #c96820)', px: 6 }}
            >
              Finish
            </Button>
          </Box>
        ) : (
          <>
            {/* Step number badge */}
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 52, height: 52, borderRadius: '50%',
              background: 'linear-gradient(135deg, #f0873c, #c96820)',
              mb: 3, flexShrink: 0,
            }}>
              <Typography variant="h6" fontWeight={800} sx={{ color: '#fff' }}>
                {step + 1}
              </Typography>
            </Box>

            {/* Step text */}
            <Typography
              variant="h5"
              sx={{ lineHeight: 1.65, mb: 4, fontWeight: 400, fontSize: { xs: '1.2rem', md: '1.4rem' } }}
            >
              {current?.text}
            </Typography>

            {/* Relevant ingredients hint (first step only) */}
            {step === 0 && ingredients.length > 0 && (
              <Box sx={{ mt: 'auto', pt: 3, borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, textTransform: 'uppercase', letterSpacing: 1 }}>
                  Ingredients needed
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {ingredients.map((ing, i) => (
                    <Chip
                      key={i}
                      label={[ing.amount, ing.unit, ing.name].filter(Boolean).join(' ')}
                      size="small"
                      sx={{ bgcolor: 'rgba(255,255,255,0.06)', fontSize: '0.78rem' }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Navigation */}
      {!isDone && (
        <Box sx={{
          display: 'flex', gap: 2, px: { xs: 3, md: 8 }, py: 3,
          borderTop: '1px solid rgba(255,255,255,0.07)',
          maxWidth: 720, mx: 'auto', width: '100%',
        }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            sx={{ flex: 1, py: 1.5, borderColor: 'rgba(255,255,255,0.15)' }}
          >
            Previous
          </Button>
          <Button
            variant="contained"
            endIcon={isLast ? <Check /> : <ArrowForward />}
            onClick={() => setStep((s) => s + 1)}
            sx={{
              flex: 2, py: 1.5,
              background: isLast
                ? 'linear-gradient(135deg, #6bb07a, #4a8a58)'
                : 'linear-gradient(135deg, #f0873c, #c96820)',
            }}
          >
            {isLast ? 'Finish Cooking' : 'Next Step'}
          </Button>
        </Box>
      )}
    </Dialog>
  );
}
